import type { ApiClientErrorCode, PaintMatchResponseItem } from '@pomoshnik/shared';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { CirclePickerOverlay } from '@/components/PhotoTools/CirclePickerOverlay';
import { RadiusSlider } from '@/components/PhotoTools/RadiusSlider';
import { MatchResults } from './MatchResults';

/**
 * Presenter PaintPhotoPicker (PRD §6 Block 3 Phase 4 Шаг 5). См. ADR-0005,
 * ADR-0008. Чистая функция от props к JSX.
 *
 * 6 internal states (P-021 + ADR-0008 §8) — симметрия с decor PhotoPickerView:
 *   - noImage:     <input type="file"> + prompt
 *   - imageLoaded: фото + 2 mode-кнопки (average / area)
 *   - pickerActive: фото + SVG circle overlay + slider + submit-кнопка
 *   - loading:     submit отправлен, ждём ответ
 *   - results:     <MatchResults> с photo + RAL badge + ΔE (Шаг 6)
 *   - error:       fixed text
 *
 * Differences vs decor PhotoPickerView:
 *   - No `kind` prop (paint один домен — ADR-0010 §1).
 *   - Header: «Подбор краски по фото» (vs decor «Подбор по фото»).
 *   - Texts: «Подобрать краску» / «Загрузите фото краски» / «Ищем похожие краски»
 *     (вместо «декор» / «декоры»).
 *   - ResultsState — <MatchResults> компонент с photo + RAL + ΔE (Шаг 6).
 */
export type PhotoPickerState =
  | 'noImage'
  | 'imageLoaded'
  | 'pickerActive'
  | 'loading'
  | 'results'
  | 'error';

export interface PhotoPickerViewProps {
  state: PhotoPickerState;
  imageUrl: string | undefined;
  imageWidth: number;
  imageHeight: number;
  cx: number;
  cy: number;
  radius: number;
  radiusPercent: number;
  matchItems?: readonly PaintMatchResponseItem[];
  error?: { code: ApiClientErrorCode; message: string };

  onFileSelect: (file: File) => void;
  onAverageMatch: () => void;
  onActivatePicker: () => void;
  onAreaMatch: () => void;
  onBackToImageLoaded: () => void;
  onCircleMove: (cx: number, cy: number) => void;
  onRadiusChange: (percent: number) => void;
  onResetImage: () => void;
  onItemClick: (globalId: string) => void;
}

export function PhotoPickerView(props: PhotoPickerViewProps) {
  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Подбор краски по фото" />
      <section className="flex-1 p-6 overflow-y-auto">
        <StateView {...props} />
      </section>
      <BottomBar />
    </main>
  );
}

function StateView(props: PhotoPickerViewProps) {
  switch (props.state) {
    case 'noImage':
      return <NoImageState onFileSelect={props.onFileSelect} />;
    case 'imageLoaded':
      return (
        <ImageLoadedState
          imageUrl={props.imageUrl ?? ''}
          onAverageMatch={props.onAverageMatch}
          onActivatePicker={props.onActivatePicker}
          onResetImage={props.onResetImage}
        />
      );
    case 'pickerActive':
      return (
        <PickerActiveState
          imageUrl={props.imageUrl ?? ''}
          imageWidth={props.imageWidth}
          imageHeight={props.imageHeight}
          cx={props.cx}
          cy={props.cy}
          radius={props.radius}
          radiusPercent={props.radiusPercent}
          onCircleMove={props.onCircleMove}
          onRadiusChange={props.onRadiusChange}
          onAreaMatch={props.onAreaMatch}
          onBackToImageLoaded={props.onBackToImageLoaded}
        />
      );
    case 'loading':
      return <LoadingState />;
    case 'results':
      return (
        <ResultsState
          items={props.matchItems ?? []}
          onItemClick={props.onItemClick}
          onResetImage={props.onResetImage}
        />
      );
    case 'error':
      return <ErrorState message={props.error?.message ?? ''} onResetImage={props.onResetImage} />;
  }
}

function NoImageState({ onFileSelect }: { onFileSelect: (file: File) => void }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-6 text-center">
      <p className="text-muted text-[18px] max-w-[280px] m-0 leading-normal">
        Загрузите фото краски — JPG или PNG
      </p>
      <input
        id="photo-input"
        type="file"
        accept="image/*"
        aria-label="Выбрать файл с фото"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFileSelect(f);
        }}
        className="block min-h-tap"
      />
    </div>
  );
}

function ImageLoadedState({
  imageUrl,
  onAverageMatch,
  onActivatePicker,
  onResetImage,
}: {
  imageUrl: string;
  onAverageMatch: () => void;
  onActivatePicker: () => void;
  onResetImage: () => void;
}) {
  return (
    <div className="flex flex-col gap-5">
      <div className="w-full max-h-[50vh] overflow-hidden rounded-xl bg-icon-bg flex items-center justify-center">
        <img
          src={imageUrl}
          alt="Загруженное фото"
          className="w-full h-auto object-contain max-h-[50vh]"
        />
      </div>
      <button
        type="button"
        onClick={onAverageMatch}
        className={[
          'w-full min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink',
          'font-bold text-[18px] py-3',
          'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
        ].join(' ')}
      >
        Подобрать краску
      </button>
      <button
        type="button"
        onClick={onActivatePicker}
        className={[
          'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
          'font-medium text-[16px] py-3',
          'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
        ].join(' ')}
      >
        Подобрать краску для конкретной детали
      </button>
      <button
        type="button"
        onClick={onResetImage}
        className="w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink text-[14px] py-3"
      >
        Загрузить другое фото
      </button>
    </div>
  );
}

function PickerActiveState({
  imageUrl,
  imageWidth,
  imageHeight,
  cx,
  cy,
  radius,
  radiusPercent,
  onCircleMove,
  onRadiusChange,
  onAreaMatch,
  onBackToImageLoaded,
}: {
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
  cx: number;
  cy: number;
  radius: number;
  radiusPercent: number;
  onCircleMove: (cx: number, cy: number) => void;
  onRadiusChange: (percent: number) => void;
  onAreaMatch: () => void;
  onBackToImageLoaded: () => void;
}) {
  return (
    <div className="flex flex-col gap-5">
      <div className="relative w-full max-h-[50vh] overflow-hidden rounded-xl bg-icon-bg">
        <img
          src={imageUrl}
          alt="Загруженное фото"
          className="w-full h-auto object-contain max-h-[50vh]"
        />
        <CirclePickerOverlay
          imageWidth={imageWidth}
          imageHeight={imageHeight}
          cx={cx}
          cy={cy}
          radius={radius}
          onMove={onCircleMove}
        />
      </div>
      <RadiusSlider radiusPercent={radiusPercent} onRadiusChange={onRadiusChange} />
      <button
        type="button"
        onClick={onAreaMatch}
        className={[
          'w-full min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink',
          'font-bold text-[18px] py-3',
          'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
        ].join(' ')}
      >
        Подобрать краски
      </button>
      <button
        type="button"
        onClick={onBackToImageLoaded}
        className="w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink text-[16px] py-3"
      >
        ← Изменить режим
      </button>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3 text-center">
      <p className="text-muted text-[18px] m-0">Ищем похожие краски…</p>
    </div>
  );
}

function ResultsState({
  items,
  onItemClick,
  onResetImage,
}: {
  items: readonly PaintMatchResponseItem[];
  onItemClick: (globalId: string) => void;
  onResetImage: () => void;
}) {
  return (
    <div className="flex flex-col gap-5">
      <MatchResults items={items} onItemClick={onItemClick} />
      <button
        type="button"
        onClick={onResetImage}
        className="w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink text-[16px] py-3"
      >
        Загрузить другое фото
      </button>
    </div>
  );
}

function ErrorState({ message, onResetImage }: { message: string; onResetImage: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center">
      <p className="text-error text-[18px] max-w-[280px] m-0 leading-normal">
        Не удалось получить ответ. Проверьте интернет и попробуйте снова.
      </p>
      {message && <p className="text-muted text-sm">{message}</p>}
      <button
        type="button"
        onClick={onResetImage}
        className="min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink font-semibold text-base px-6 py-3"
      >
        Загрузить другое фото
      </button>
    </div>
  );
}
